import { getLoginToken } from '@/components/shared/getToken';

import {
  addEventsToQueue,
  getBatch,
  removeEventsFromQueue,
  validateEventInDev,
} from '@/utils/Events/eventQueue';
import {
  clearGuestEventQueue,
  drainGuestEventQueue,
} from '@/utils/Events/guestEventQueue';

import { activityLogsApiClient } from '../apiClients';

const PROCESS_INTERVAL =
  parseInt(process.env.NEXT_PUBLIC_PROCESS_INTERVAL as string) || 15000; // second
let isProcessing = false;

// Multi-tab channel for guest events synchronization
let guestChannel: BroadcastChannel | null = null;
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  guestChannel = new BroadcastChannel('guest_events_channel');
  guestChannel.onmessage = (event) => {
    if (event.data?.type === 'GUEST_EVENTS_FLUSHED') {
      clearGuestEventQueue();
    }
  };
}

/**
 * Processes a single batch of events from the main queue if the user is authenticated.
 */
export const processBatch = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  if (isProcessing) return;

  const batch = getBatch();
  if (!batch.length) return;

  try {
    const token = await getLoginToken();
    if (!token) return; // Only send when authenticated
  } catch (error) {
    return;
  }

  isProcessing = true;

  try {
    // 1. Send bulk to backend
    await activityLogsApiClient.post('/bulk', batch);

    // 2. Remove successfully sent events
    removeEventsFromQueue(batch.length);

    // 3. If there are more items in queue, continue processing
    const remainingBatch = getBatch();
    if (remainingBatch.length > 0) {
      setTimeout(() => {
        processBatch();
      }, 500);
    }
  } catch (error) {
    console.error('Bulk event send failed, will retry:', error);
  } finally {
    isProcessing = false;
  }
};

/**
 * Drains stored unauthenticated events from the guest queue,
 * enriches them with zl_uid and user credentials, moves them to the main event queue,
 * and triggers immediate dispatch.
 */
export const flushAndHydrateGuestEvents = async ({
  zl_uid,
  userId,
}: {
  zl_uid: string;
  userId?: string | number;
}) => {
  if (typeof window === 'undefined' || !zl_uid) return;

  try {
    const guestEvents = drainGuestEventQueue();
    if (!guestEvents.length) return;

    // Notify other open tabs that guest events have been drained
    if (guestChannel) {
      guestChannel.postMessage({ type: 'GUEST_EVENTS_FLUSHED' });
    }

    const hydratedEvents = guestEvents.map((event) => {
      const { _guest_queued_at, ...restEvent } = event;
      return {
        ...restEvent,
        zl_uid: zl_uid,
        ...(userId ? { user_id: userId } : {}),
        meta: {
          ...(restEvent.meta || {}),
          was_guest: true,
          replayed_at: Date.now(),
        },
      };
    });

    // In development / staging, validate each complete replayed event one by one
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      hydratedEvents.forEach((event) => {
        validateEventInDev(event, '[Replayed Event]');
      });
    }

    addEventsToQueue(hydratedEvents);

    // Trigger immediate batch flush to avoid waiting for interval
    await processBatch();
  } catch (error) {
    console.error('Failed to flush and hydrate guest events:', error);
  }
};

export const startEventProcessor = () => {
  if (typeof window === 'undefined') return;

  setInterval(async () => {
    await processBatch();
  }, PROCESS_INTERVAL);
};
